import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil, faTrash, faSpinner, faIndustry } from '@fortawesome/free-solid-svg-icons';
import {
  manufacturerService,
  type Manufacturer,
  type CreateManufacturerPayload,
} from '@/services/manufacturer.service';
import { SkeletonPage } from '@/components/skeletons';
import { PageHeader } from '@/components/shared/PageHeader';
import { SearchBar } from '@/components/shared/SearchBar';
import { EmptyState } from '@/components/shared/EmptyState';
import { Pagination } from '@/components/shared/Pagination';
import { Modal } from '@/components/shared/Modal';
import { FormField } from '@/components/shared/FormField';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { toast, confirmDelete } from '@/lib/swal';
import { useDebounce } from '@/hooks/use-debounce';
import { useHasPermission } from '@/hooks/use-permissions';

const PAGE_SIZE = 20;

/** Exported for reuse by the inline creation modals (Phase C.2) — the intake
 *  workflow must never duplicate this form. */
export function ManufacturerForm({
  initial,
  onSubmit,
  onCancel,
  saving,
}: {
  initial?: Partial<CreateManufacturerPayload>;
  onSubmit: (d: CreateManufacturerPayload) => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [nameErr, setNameErr] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setNameErr('Name is required');
      return;
    }
    setNameErr('');
    onSubmit({ name, description: description || undefined });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <FormField label="Name" htmlFor="m-name" required error={nameErr}>
        <Input
          id="m-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Siemens"
          disabled={saving}
        />
      </FormField>
      <FormField label="Description" htmlFor="m-desc">
        <Textarea
          id="m-desc"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          disabled={saving}
        />
      </FormField>
      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button type="submit" disabled={saving} className="min-w-[100px]">
          {saving ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : 'Save'}
        </Button>
      </div>
    </form>
  );
}

export function ManufacturersPage() {
  const queryClient = useQueryClient();
  const can = useHasPermission();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Manufacturer | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['manufacturers', page, debouncedSearch],
    queryFn: () =>
      manufacturerService.list({ page, pageSize: PAGE_SIZE, search: debouncedSearch || undefined }),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['manufacturers'] });

  const createMutation = useMutation({
    mutationFn: manufacturerService.create,
    onSuccess: () => {
      toast.success('Manufacturer created');
      setModalOpen(false);
      invalidate();
    },
    onError: () => toast.error('Failed to create manufacturer'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<CreateManufacturerPayload> }) =>
      manufacturerService.update(id, payload),
    onSuccess: () => {
      toast.success('Manufacturer updated');
      setModalOpen(false);
      setEditTarget(null);
      invalidate();
    },
    onError: () => toast.error('Failed to update manufacturer'),
  });

  const deleteMutation = useMutation({
    mutationFn: manufacturerService.remove,
    onSuccess: () => {
      toast.success('Manufacturer deleted');
      invalidate();
    },
    onError: () => toast.error('Failed to delete manufacturer'),
  });

  const handleDelete = async (m: Manufacturer) => {
    if (await confirmDelete(m.name)) deleteMutation.mutate(m.id);
  };

  const openCreate = () => {
    setEditTarget(null);
    setModalOpen(true);
  };
  const openEdit = (m: Manufacturer) => {
    setEditTarget(m);
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setEditTarget(null);
  };
  const handleSearch = (v: string) => {
    setSearch(v);
    setPage(1);
  };
  const saving = createMutation.isPending || updateMutation.isPending;

  if (isLoading) return <SkeletonPage />;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Manufacturers"
        description="Equipment and parts manufacturers"
        onAdd={can('manufacturer:create') ? openCreate : undefined}
        addLabel="Add Manufacturer"
      />
      <SearchBar value={search} onChange={handleSearch} placeholder="Search manufacturers..." />

      {data && data.total > 0 ? (
        <>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full min-w-[480px] text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left">
                  <th className="px-4 py-3 font-medium text-foreground">Name</th>
                  <th className="px-4 py-3 font-medium text-foreground">Description</th>
                  <th className="px-4 py-3 font-medium text-foreground">Status</th>
                  <th className="px-4 py-3 font-medium text-foreground sr-only">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((m) => (
                  <tr key={m.id} className="border-b border-border last:border-0 hover:bg-muted/20">
                    <td className="px-4 py-3 font-medium text-foreground">{m.name}</td>
                    <td className="px-4 py-3 max-w-xs truncate text-muted-foreground">
                      {m.description ?? '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${m.isActive ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}
                      >
                        {m.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {can('manufacturer:update') && (
                          <button
                            onClick={() => openEdit(m)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
                            aria-label={`Edit ${m.name}`}
                          >
                            <FontAwesomeIcon icon={faPencil} className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {can('manufacturer:delete') && (
                          <button
                            onClick={() => handleDelete(m)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                            aria-label={`Delete ${m.name}`}
                            disabled={deleteMutation.isPending}
                          >
                            <FontAwesomeIcon icon={faTrash} className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            page={data.page}
            pageSize={data.pageSize}
            total={data.total}
            onPageChange={setPage}
          />
        </>
      ) : (
        <EmptyState
          icon={faIndustry}
          title={search ? 'No manufacturers match your search' : 'No manufacturers yet'}
          description={
            search ? 'Try a different search term.' : 'Add your first manufacturer to get started.'
          }
          action={
            !search && can('manufacturer:create') ? (
              <Button onClick={openCreate}>Add Manufacturer</Button>
            ) : undefined
          }
        />
      )}

      <Modal
        open={modalOpen}
        onClose={closeModal}
        title={editTarget ? 'Edit Manufacturer' : 'Add Manufacturer'}
      >
        <ManufacturerForm
          initial={
            editTarget
              ? { name: editTarget.name, description: editTarget.description ?? undefined }
              : undefined
          }
          onSubmit={(p) =>
            editTarget
              ? updateMutation.mutate({ id: editTarget.id, payload: p })
              : createMutation.mutate(p)
          }
          onCancel={closeModal}
          saving={saving}
        />
      </Modal>
    </div>
  );
}
